/**
 * LAYER B · PIECE D — a due date predicted from how THIS PERSON works.
 *
 * The board already suggests a due date from the cards that look like the new one
 * (src/suggest.ts): its phase end, its sprint end, or the median duration of
 * comparable cards. Those are read off the WORK. This reads off the PERSON — because
 * the same "Medium" card takes one doer four working days and another nine, and a
 * due date the owner will actually hit is the one shaped by their own pace, not the
 * board's average.
 *
 * THE MODEL, STATED PLAINLY. Take the cards this owner has already FINISHED, of the
 * SAME SIZE, and measure how long each actually took in WORKING DAYS from the day it
 * was started to the day it was done (the owner's live calendar, so weekends and the
 * company shutdown never count as work). The middle of that spread (the median) is
 * how long a card like this takes them; the quarter either side (p25–p75) is the
 * honest range around it. The forecast is simply TODAY plus that many working days,
 * landed on a working day. "~18 Nov — about how long Marcia's Medium cards take, 14
 * of them" is a basis a person can check and believe.
 *
 * WHY ELAPSED, NOT "PURE WORKING SPEED". The span start→done already contains the
 * interruptions a card of this kind really meets for this person — the hand-offs, the
 * waits, the days half of it went elsewhere. A due date is a promise about the
 * CALENDAR, so it must carry those; stripping them out would model a speed the work
 * never actually moves at and promise a date it never actually hits. (Modelling the
 * intrinsic stage-by-stage pace is a later refinement for the "coming up" read, not
 * for a date someone commits to.)
 *
 * CONFIDENCE-GATED, exactly like src/suggest.ts. A due date is a thing the owner will
 * be measured against, so a thin or lop-sided sample must offer NOTHING rather than a
 * number dressed up as personal. Below PERSONAL_MIN_SAMPLE finished cards of that
 * size the function returns null and the caller falls through to the board-shaped
 * sources — a blank is a better answer than a confident-looking guess.
 *
 * THE OWNER IS THE DOER, NOT WHOEVER TOUCHED IT. A card's owner is its Responsible
 * (`responsible`), falling back to its `assignee` — the person whose pace the date is
 * a promise about — never the `who` that happened to move it between columns.
 *
 * PURE ON PURPOSE, like src/suggest.ts / src/idle-nudge.ts / src/dependencies.ts — no
 * store, no React, no clock of its own. The caller passes the owner, the size, the
 * pool of finished cards to learn from (board-local today, cross-board as the
 * portfolio read widens), `now`, and the working calendar. That is what lets the
 * model be proved against fixtures in scripts/test-forecast.ts rather than argued
 * about. The working-day maths is reused from src/work-calendar.ts, never reinvented.
 */
import type { Issue, IssueSize } from './types';
import {
  type WorkCalendar,
  DEFAULT_CALENDAR,
  workingDaysBetween,
  addWorkingDays,
  nextWorkingDay,
} from './work-calendar';
import { today, inSprint, type Sprint } from './sprints';
import { forecastScoped, ragForProb, type Rag } from './insights';
import { readyFirst } from './next-up';
import { healthOfIssue } from './progress-health';
import { lastMovementMs, businessDaysBetween } from './idle-nudge';
import { stillBlocked } from './blocked';

/**
 * HOW MANY FINISHED CARDS OF THAT SIZE A PERSONAL FORECAST NEEDS before it is trusted
 * (Vinnie, 31 Oct 2026 — "~5 of that size"). Below this the spread is one person's
 * handful of cards, not a pace: the median swings on a single outlier and the range
 * is noise, so the forecaster withholds and the board-shaped sources answer instead.
 */
export const PERSONAL_MIN_SAMPLE = 5;

/**
 * THE SLICE OF A CARD THE FORECASTER LEARNS FROM — a Pick of Issue, so a real Issue
 * passes straight in and a fixture only carries these fields (the suggest.ts idiom).
 * A card contributes to a person's pace only when it is FINISHED and carries both the
 * day it started and the day it completed; everything else is read to decide whether
 * it counts and whose pace it counts toward.
 */
export type HistoryCard = Pick<
  Issue,
  'id' | 'kind' | 'size' | 'status' | 'startedAt' | 'completedAt' | 'assignee' | 'responsible'
>;

/**
 * A PERSONAL DUE-DATE FORECAST. `due` is the day to offer (the p50); `low`/`high` are
 * the p25/p75 days that bracket it, for a surface with room to show a range; `days`
 * is the median working-day span the date came from; `sample` is how many finished
 * cards stood behind it; `owner` is the person modelled, for the basis sentence.
 */
export interface PersonalDueForecast {
  due: string;
  low: string;
  high: string;
  days: number;
  sample: number;
  owner: string;
}

/** The owner whose pace a card's date is a promise about: its doer (Responsible),
 *  else its assignee, else '' (no one to model — never a forecast). */
export function ownerOf(card: Pick<Issue, 'assignee' | 'responsible'>): string {
  const doer = card.responsible && card.responsible.length > 0 ? card.responsible : card.assignee;
  return doer ?? '';
}

/** Finished work — only a `done` card has a real start→complete span to learn from;
 *  a dropped (`wontdo`) card was abandoned, so its span is not a pace. */
function isDone(card: HistoryCard): boolean {
  return card.status === 'done';
}

/**
 * THE NEAREST-RANK VALUE at percentile `p` (0..1) of an ascending array — the same
 * rule the forecast engine uses, so a percentile means one thing across the app. No
 * interpolation: it returns an element that actually occurred, which is what "a card
 * like this took that long" has to mean. The index is clamped into range so p75 of a
 * short sample lands on the last element rather than off the end.
 */
function nearestRank(sortedAsc: number[], p: number): number {
  if (sortedAsc.length === 0) return 0;
  const idx = Math.min(sortedAsc.length - 1, Math.max(0, Math.ceil(p * sortedAsc.length) - 1));
  return sortedAsc[idx];
}

/**
 * PREDICT A DUE DATE for a new card of size `size` owned by `owner`, from that owner's
 * own finished cards of the same size in `history`.
 *
 * Returns null — "show nothing, let the next source answer" — when:
 *   · there is no owner to model (an unassigned card has no personal pace), or
 *   · fewer than PERSONAL_MIN_SAMPLE of the owner's finished cards of that size carry
 *     both a start and a completion day (the sample is too thin to stand behind).
 *
 * Otherwise the span of each comparable is its WORKING days from start to completion
 * on the given calendar; the forecast is today + the median of those spans, placed on
 * a working day, with the p25/p75 days as the honest range around it.
 *
 * `now` and `cal` are injectable so the rule is deterministic under test; `cal`
 * defaults to the plain Mon–Fri calendar when a workspace has set none.
 */
export function personalDueForecast(
  owner: string,
  size: IssueSize,
  history: HistoryCard[],
  now: number = Date.now(),
  cal: WorkCalendar = DEFAULT_CALENDAR,
): PersonalDueForecast | null {
  if (!owner || !size) return null; // no one to model, or no size to segment by

  // The owner's OWN finished cards of this size that carry a measurable span. Size is
  // the first and only segment (Vinnie, 31 Oct — "segment SIZE-first"); the owner is
  // matched by the same doer rule the date will be a promise about.
  const spans = ownerCycleDays(owner, size, history, cal);

  if (spans.length < PERSONAL_MIN_SAMPLE) return null;

  const p50 = nearestRank(spans, 0.5);
  const p25 = nearestRank(spans, 0.25);
  const p75 = nearestRank(spans, 0.75);

  // From today forward, in the owner's working days — a date they could hit if the
  // work starts now. nextWorkingDay keeps a zero-day edge case off a weekend/holiday.
  const from = nextWorkingDay(today(now), cal);
  return {
    due: addWorkingDays(from, p50, cal),
    low: addWorkingDays(from, p25, cal),
    high: addWorkingDays(from, p75, cal),
    days: p50,
    sample: spans.length,
    owner,
  };
}

/**
 * HOW MANY COMPARABLE CARDS A CYCLE-TIME PERCENTILE NEEDS before it is trusted. A
 * percentile wants a few more than a median — five is the floor the whole forecaster
 * uses, and below it the "85% of the time" claim is a sample too small to carry it.
 */
export const CYCLE_MIN_SAMPLE = 5;

/**
 * A CYCLE-TIME "FINISH-BY" FORECAST (Layer B, piece C). `due` is today + the `pct`-th
 * percentile working-day cycle of comparable cards — a day most cards like this finish
 * by; `days` is that cycle; `sample` is how many cards stood behind it; `pct` is the
 * confidence as a whole-number percent (85), for the basis sentence.
 */
export interface CycleFinishBy {
  due: string;
  days: number;
  sample: number;
  pct: number;
}

/**
 * THE WORKING-DAY SPAN a finished card actually took, start → completion, on the given
 * calendar — or null when it is not a finished card carrying both ends. At least one
 * day: a card done the day it was started still took a day of work. Shared by the
 * personal (per-owner) and the cycle-time (comparable) reads so "how long it took"
 * means one thing.
 */
function cycleWorkingDays(c: HistoryCard, cal: WorkCalendar): number | null {
  if (!isDone(c) || typeof c.startedAt !== 'number' || typeof c.completedAt !== 'number' || c.completedAt < c.startedAt) {
    return null;
  }
  return Math.max(1, workingDaysBetween(c.startedAt, c.completedAt, cal));
}

/**
 * THE OWNER'S OWN WORKING-DAY CYCLES at a given size, sorted — how long THIS PERSON's cards of
 * that size have actually taken, start → done. The personal counterpart to `comparableCycles`
 * (which reads the whole board): shared by the personal due-date forecast (piece D) and the
 * release reconcile's trajectory floor (piece A, Stage 2), so "how long this person takes" means
 * one thing. Empty when there is no owner or size, or no finished cards of theirs to measure.
 */
export function ownerCycleDays(
  owner: string,
  size: IssueSize,
  history: HistoryCard[],
  cal: WorkCalendar = DEFAULT_CALENDAR,
): number[] {
  if (!owner || !size) return [];
  return history
    .filter((c) => ownerOf(c) === owner && c.size === size)
    .map((c) => cycleWorkingDays(c, cal))
    .filter((d): d is number => d != null)
    .sort((a, b) => a - b);
}

/**
 * PREDICT A "FINISH-BY" DATE for a new card of this `kind` and `size`, from how long
 * COMPARABLE cards actually took — "cards like this finish within N days 85% of the
 * time". Unlike the personal read it is not tied to one owner, so it answers when a
 * doer has no history of their own but the board (and, as the pool widens, the org)
 * does; `pct` is the confidence (default the 85% commit line).
 *
 * Returns null — "show nothing, let the next source answer" — when there is no size to
 * segment by, or fewer than CYCLE_MIN_SAMPLE comparable finished cards carry a cycle.
 *
 * Comparability is SIZE-first (Vinnie, 31 Oct): a card's size is the strongest signal
 * of how long it runs. It narrows to the SAME KIND only when that tighter set is itself
 * big enough to stand behind; otherwise a card of this size, any kind, is the comparison.
 */
/**
 * THE WORKING-DAY CYCLES OF COMPARABLE FINISHED CARDS, sorted — the spread "a card like
 * this" actually ran for. SIZE-first (a card's size is the strongest signal of how long it
 * runs); narrows to the SAME KIND only when that tighter set is itself big enough to stand
 * behind. Shared by the cycle-time finish-by (piece C) and the release reconcile (piece A),
 * so "how long a comparable card takes" means one thing. Empty when there is no size, or when
 * the comparable set is too thin to trust.
 */
export function comparableCycles(
  kind: Issue['kind'],
  size: IssueSize,
  history: HistoryCard[],
  cal: WorkCalendar = DEFAULT_CALENDAR,
): number[] {
  if (!size) return [];
  const bySize = history
    .map((c) => ({ c, d: cycleWorkingDays(c, cal) }))
    .filter((x): x is { c: HistoryCard; d: number } => x.d != null && x.c.size === size);
  const byKindSize = bySize.filter((x) => x.c.kind === kind);
  const set = byKindSize.length >= CYCLE_MIN_SAMPLE ? byKindSize : bySize;
  if (set.length < CYCLE_MIN_SAMPLE) return [];
  return set.map((x) => x.d).sort((a, b) => a - b);
}

export function cycleTimeFinishBy(
  kind: Issue['kind'],
  size: IssueSize,
  history: HistoryCard[],
  now: number = Date.now(),
  cal: WorkCalendar = DEFAULT_CALENDAR,
  pct = 0.85,
): CycleFinishBy | null {
  const cycles = comparableCycles(kind, size, history, cal);
  if (cycles.length < CYCLE_MIN_SAMPLE) return null;

  const days = nearestRank(cycles, pct);
  const from = nextWorkingDay(today(now), cal);
  return { due: addWorkingDays(from, days, cal), days, sample: cycles.length, pct: Math.round(pct * 100) };
}

/** Why a card gets an at-risk read instead of a number, so the surface can phrase it plainly.
 *  `over-cycle` — a started card that has already been in progress longer than cards like it take. */
export type AtRiskReason = 'overdue-unstarted' | 'overdue-stalled' | 'stalled' | 'behind-unstarted' | 'over-cycle';

/** How long an aged card has been in progress vs how long cards like it usually take, both in
 *  working days — carried on an `over-cycle` read so the surface can show the real numbers. */
export interface CycleAge {
  inProgressWd: number;
  typicalWd: number;
}

/**
 * WHY A CARD CAN'T HONESTLY CARRY A FORECAST NUMBER — the state gate, shared by the per-card
 * release outlook and the sprint odds so "stuck" means one thing everywhere. Null means the card
 * is moving (or not started but on time) and a number is honest; otherwise it is `paused` (blocked
 * or on hold — no finish until that changes) or `at-risk` (behind AND not moving, OR already past
 * the time cards like it take, where a flow-average percentage would contradict the card's own
 * state). Carries no target: the caller adds the deadline it is judged against (go-live for a card,
 * sprint end for a sprint).
 */
export type StuckState =
  | { kind: 'paused'; reason: 'blocked' | 'on-hold' }
  | { kind: 'at-risk'; reason: AtRiskReason; age?: CycleAge };

/**
 * THE STATE GATE. Reads only the card's own state — held, blocked, started, overdue, health tone,
 * last movement, and how long it has been in progress — never the queue or a deadline, so the same
 * verdict serves a card's release outlook and a sprint's finish odds. Returns null when the card is
 * moving (or not started but on time); otherwise the honest non-number read. Settled cards
 * (done/abandoned) are the caller's to skip and read as moving here.
 *
 * AGE IS A FIRST-CLASS STUCK SIGNAL. A started card that has already been in progress longer than
 * ~85% of comparable cards take has blown its own cycle — it is in the slow tail and empirically
 * unlikely to finish soon, so it cannot carry a confident number no matter how recently it was
 * touched or what its progress bar reads. This is the standard flow-metrics "item age vs cycle-time
 * percentile" at-risk signal; the board's Aging panel already flags these cards, and reading the age
 * off the SAME owner/board cycle spread the trajectory uses keeps the two in step. Checked before the
 * overdue/stalled reasons because "running longer than cards like this take" is the more useful word.
 */
export function cardStuckState(
  card: Issue,
  boardIssues: Issue[],
  now: number,
  cal: WorkCalendar,
): StuckState | null {
  if (card.status === 'done' || card.status === 'wontdo') return null; // settled, not stuck
  if (card.heldAt != null) return { kind: 'paused', reason: 'on-hold' };
  if (stillBlocked(card, boardIssues)) return { kind: 'paused', reason: 'blocked' };

  const notStarted = card.startedAt == null;

  // AGED OUT — in progress past the time cards like it take. Read from the owner's own cycle spread
  // where they have a track record, else the board's comparable cards (the same source the
  // trajectory floor uses), gated on enough history to say.
  if (card.startedAt != null && card.size) {
    const owner = ownerOf(card);
    const own = ownerCycleDays(owner, card.size, boardIssues, cal);
    const cycles = own.length >= PERSONAL_MIN_SAMPLE ? own : comparableCycles(card.kind, card.size, boardIssues, cal);
    if (cycles.length >= CYCLE_MIN_SAMPLE) {
      const typicalWd = nearestRank(cycles, 0.85);
      const inProgressWd = workingDaysBetween(card.startedAt, now, cal);
      if (inProgressWd >= typicalWd) {
        return { kind: 'at-risk', reason: 'over-cycle', age: { inProgressWd, typicalWd } };
      }
    }
  }

  const overdue = !!card.dueDate && card.dueDate < today(now); // past its own due day, still open
  const health = healthOfIssue(card, now, undefined, cal);
  const behind = overdue || health.tone === 'bad';
  const stalled = !notStarted && businessDaysBetween(lastMovementMs(card), now) >= STALL_WORKING_DAYS;
  const notMoving = notStarted || stalled;

  if (behind && notMoving) {
    const reason: AtRiskReason = overdue
      ? notStarted ? 'overdue-unstarted' : 'overdue-stalled'
      : notStarted ? 'behind-unstarted' : 'stalled';
    return { kind: 'at-risk', reason };
  }
  return null;
}

/**
 * WHAT THE RELEASE OUTLOOK CAN HONESTLY SAY about one card (Layer B · piece A). It is one of
 * three shapes, because a single percentage is not an honest answer for every card:
 *   · `odds`    — a real chance, for a card that is moving, or not started but still on time.
 *   · `at-risk` — NO number, because the card is already behind AND not moving. An overdue or
 *                 stalled card's own state is the honest answer; a flow-average percentage
 *                 beside its Overdue badge would be a contradiction, not information.
 *   · `paused`  — no outlook at all, because the card is blocked or on hold and has no honest
 *                 finish until that changes; its own blocked/hold banner is the true thing.
 * `tentative` on `odds` marks a card that has not started: the chance is real but less settled,
 * because it carries the time to pick the work up as well as to do it. `pace` names the cadence
 * the trajectory floor was read from (the owner's own, or the board's comparable cards) for the
 * basis line; `owner` + `ownerLoad` carry how many OTHER open cards the owner is carrying on this
 * board, a shown caveat (never silently folded into the number — see releaseOutlook).
 */
export type ReleaseOutlook =
  | {
      kind: 'odds';
      prob: number;
      rag: Rag;
      rank: number;
      ahead: number;
      target: number;
      tentative: boolean;
      pace: TrajectoryFit | null;
      owner: string;
      ownerLoad: number;
    }
  | { kind: 'at-risk'; target: number; reason: AtRiskReason; age?: CycleAge }
  | { kind: 'paused'; reason: 'blocked' | 'on-hold' };

/**
 * HOW MANY WORKING DAYS OF QUIET make a started card "not moving" for the outlook — a working
 * week. Below it the card is in normal flow; at or past it nothing has moved, and a confident
 * flow percentage would be a fiction. Movement is read the way the idle nudge reads it
 * (`lastMovementMs`), so the two agree on what "quiet" means.
 */
export const STALL_WORKING_DAYS = 5;

/** Which cadence the trajectory floor was read from, and how many cards stood behind it — so
 *  the surface can name the basis ("at Marcia's own pace, 7 of her Medium cards"). */
export interface TrajectoryFit {
  prob: number;
  /** 'owner' — read from the owner's OWN finished cards; 'board' — from comparable cards board-wide. */
  source: 'owner' | 'board';
  sample: number;
}

/**
 * THE CHANCE THE CARD'S OWN WORK FITS before the go-live — the card-trajectory half of the
 * reconcile. Not "will the queue reach it" (that is the flow odds) but "if it runs at the pace
 * cards like it actually run, does what is LEFT of it fit in the working days left to go-live?"
 *
 * WHOSE PACE (Stage 2). The OWNER'S OWN finished cards of that size when they have enough of a
 * track record (`PERSONAL_MIN_SAMPLE`) — because the same Medium takes one person four working
 * days and another nine, and this card is theirs. Otherwise the board-wide comparable cycles
 * (`comparableCycles`), so a card whose owner has no history of their own still gets an honest
 * read. The chosen spread is scaled by how much of the card is still to do, so a barely-started
 * card needs a near-full cycle and a mostly-done one needs little.
 *
 * Null when neither the owner nor the board has enough comparable finished cards — then the flow
 * odds stand alone. The release odds are the WORSE of this and the flow (a card lands only if the
 * queue reaches it AND its own remaining work fits the time left).
 */
function cardOwnFitProb(
  card: Pick<Issue, 'kind' | 'size' | 'progressPct'>,
  owner: string,
  notStarted: boolean,
  history: HistoryCard[],
  goLive: number,
  now: number,
  cal: WorkCalendar,
): TrajectoryFit | null {
  if (!card.size) return null;
  // The owner's own pace first, falling back to the board's comparable cards.
  const own = ownerCycleDays(owner, card.size, history, cal);
  const usingOwner = own.length >= PERSONAL_MIN_SAMPLE;
  const cycles = usingOwner ? own : comparableCycles(card.kind, card.size, history, cal);
  if (cycles.length < CYCLE_MIN_SAMPLE) return null;
  const source = usingOwner ? 'owner' : 'board';
  const availableWD = workingDaysBetween(now, goLive, cal);
  if (availableWD <= 0) return { prob: 0, source, sample: cycles.length }; // release is here — can't fit
  // How much of the card is still to do. A card that has NOT started needs a FULL cycle whatever
  // its progress bar says — real progress accrues once the work is under way, and trusting a manual
  // percentage on a not-started card would shrink its remaining work and read too optimistically
  // (the accuracy a management estimate turns on). A started card is shortened by its real progress.
  const remainingFrac = notStarted ? 1 : Math.min(1, Math.max(0, 1 - (card.progressPct ?? 0) / 100));
  const fit = cycles.filter((c) => Math.max(1, Math.round(c * remainingFrac)) <= availableWD).length;
  return { prob: fit / cycles.length, source, sample: cycles.length };
}

/**
 * WILL THIS CARD MAKE THE RELEASE? — reconciled with the card's own state, so it can never read
 * more optimistic than the card plainly is.
 *
 * THE STATE DECIDES WHAT IS HONEST FIRST, before any arithmetic:
 *   · blocked / on hold → `paused`: no finish until that changes, and the card already says so.
 *   · behind AND not moving (overdue or pace-late, and unstarted or stalled) → `at-risk`: the
 *     card's own state is the answer, not a flow-average number that would contradict it.
 *   · otherwise → `odds`: a real chance.
 *
 * THE NUMBER, WHEN THERE IS ONE, IS THE WORSE OF TWO READS. The FLOW: a card is done once the
 * `rank` cards at or ahead of it in the board's one canonical work order (`readyFirst`) are, and
 * that chance comes from the SAME Monte-Carlo resample of board throughput the go-live panel runs
 * (`forecastScoped`) — so it can never quarrel with the project forecast and is monotone by queue
 * depth (a card nearer the front reads no worse than one behind it). The TRAJECTORY
 * (`cardOwnFitProb`): whether the card's own remaining work fits the time left, from how long
 * comparable cards actually ran. The odds are `min` of the two, because both must hold.
 *
 * WITHHOLDS (null) for a settled card (done/abandoned), no go-live, or too little finished
 * history to resample (the forecast's own cold-start gate). PURE: the caller passes the card,
 * the board's cards, the resolved go-live (`goLiveTargetMs`, the one the panel uses), `now`, a
 * seed key, the calendar, and whether backlog counts as remaining work. Proved in
 * scripts/test-forecast.ts.
 */
export function releaseOutlook(
  card: Issue,
  boardIssues: Issue[],
  goLive: number | null | undefined,
  now: number = Date.now(),
  seedKey = '',
  cal: WorkCalendar = DEFAULT_CALENDAR,
  countBacklog = true,
): ReleaseOutlook | null {
  if (goLive == null) return null; // no release date → no "will it make it" to answer
  if (card.status === 'done' || card.status === 'wontdo') return null; // settled, not a forecast

  // THE STATE DECIDES FIRST. A paused or at-risk card gets its honest non-number read, judged
  // against the go-live; only a moving (or on-time, not-started) card earns a percentage.
  const stuck = cardStuckState(card, boardIssues, now, cal);
  if (stuck) {
    return stuck.kind === 'paused' ? stuck : { kind: 'at-risk', target: goLive, reason: stuck.reason, age: stuck.age };
  }
  const notStarted = card.startedAt == null;

  // A NUMBER — the card is moving, or not started but still on time. The queue sets the flow
  // odds; the card's own trajectory floors them.
  const ordered = readyFirst(boardIssues);
  const idx = ordered.findIndex((i) => i.id === card.id);
  if (idx < 0) return null; // not in the startable queue (shouldn't reach here past the gates)
  const rank = idx + 1;
  const scope = ordered.slice(0, rank);
  const fc = forecastScoped(boardIssues, scope, now, goLive, 12, undefined, undefined, seedKey, cal, countBacklog);
  if (!fc.ready || fc.targetProb == null) return null; // cold start → withhold

  // The trajectory floor, read at the OWNER's own pace where they have a track record (Stage 2),
  // else the board's comparable cards. The odds are the worse of flow and trajectory.
  const owner = ownerOf(card);
  const ownFit = cardOwnFitProb(card, owner, notStarted, boardIssues, goLive, now, cal);
  const prob = ownFit == null ? fc.targetProb : Math.min(fc.targetProb, ownFit.prob);

  // HOW MUCH ELSE THE OWNER IS CARRYING on this board — their other open (not done/abandoned)
  // cards. A SHOWN caveat, never folded silently into the number: the owner's own historical pace
  // already prices in their normal juggling (an elapsed span carries the interruptions it really
  // met), and the true cross-board overload picture needs a cross-board read this surface does not
  // have yet. So the number stays honest and the load rides alongside it as context.
  const ownerLoad = owner
    ? boardIssues.filter((i) => i.id !== card.id && ownerOf(i) === owner && i.status !== 'done' && i.status !== 'wontdo').length
    : 0;

  return {
    kind: 'odds',
    prob,
    rag: ragForProb(prob),
    rank,
    ahead: rank - 1,
    target: goLive,
    tentative: notStarted,
    pace: ownFit,
    owner,
    ownerLoad,
  };
}

/**
 * HOW LIKELY A SPRINT IS TO FINISH (Layer B, piece B). `prob` is the chance ALL of the
 * sprint's still-open cards are done by its end date, 0..1; `rag` is the verdict that
 * chance earns on the app's one cut; `remaining` is how many open cards are riding on it;
 * `stuck` is how many of those are blocked, on hold, or behind-and-not-moving (the sprint
 * cannot honestly finish everything while any card is stuck, so the verdict is never green
 * while `stuck` > 0); `target` is the sprint-end instant the chance is measured against.
 */
export interface SprintOdds {
  prob: number;
  rag: Rag;
  remaining: number;
  stuck: number;
  target: number;
}

/**
 * THE LOCAL-MIDNIGHT INSTANT a sprint day ENDS — the day after `isoDay` at 00:00, so a card
 * finished any time ON the end day still counts (a sprint runs through the whole of its last
 * day). Local, like the go-live target, so the deadline sits on the same axis the Monte-Carlo
 * finishes do and isn't read a day early west of Greenwich. Null on a malformed day.
 */
function sprintEndMs(isoDay: string): number | null {
  const [y, mo, d] = isoDay.split('-').map(Number);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d + 1).getTime(); // the +1 rolls the month/year over for us
}

/**
 * WILL THIS SPRINT FINISH? — the chance the sprint's remaining open cards are all done by its
 * end date, read from the board's own pace. The simplest Layer-B read: unlike a card (piece A)
 * a sprint has no queue position to find — its scope is just the cards committed to it. So the
 * CADENCE is the board's weekly throughput and the SCOPE is this sprint's open cards, run
 * through the SAME Monte-Carlo resample the go-live and per-card odds use (forecastScoped) — one
 * model, so a sprint's odds, a card's odds and the release forecast all move together.
 *
 * HONEST ABOUT STUCK CARDS. The throughput resample treats every open card as an interchangeable
 * unit the board's pace will consume — but a blocked, held, or behind-and-not-moving card will NOT
 * finish just because the board is quick, so a pace-only "72% to finish" overstates a sprint that
 * is carrying stuck work. So the sprint reads each open card through the shared state gate
 * (`cardStuckState`), counts those that are stuck, and — because finishing EVERYTHING is the bar —
 * never shows a green verdict while any card is stuck (it drops to warn at worst). The number still
 * comes from the one shared forecast, so a sprint's odds and the release forecast stay in step.
 *
 * Withholds (null) when: the sprint has no valid end date; the forecast is below its cold-start
 * gate (too little finished history to resample); or nothing is left open in the sprint (then it
 * is finished or empty, not a forecast). `inSprint` is the one canonical sprint-membership rule
 * (src/sprints.ts), so the cards counted here are exactly the cards the board shows in the sprint.
 */
export function sprintOdds(
  sprint: Pick<Sprint, 'id' | 'endsOn'>,
  boardIssues: Issue[],
  now: number = Date.now(),
  seedKey = '',
  cal: WorkCalendar = DEFAULT_CALENDAR,
  countBacklog = true,
): SprintOdds | null {
  const target = sprintEndMs(sprint.endsOn);
  if (target == null) return null;

  const scope = inSprint(boardIssues, sprint.id);
  const fc = forecastScoped(boardIssues, scope, now, target, 12, undefined, undefined, seedKey, cal, countBacklog);
  if (!fc.ready || fc.targetProb == null || fc.rag == null || fc.remaining === 0) return null;

  // How many committed cards are stuck — blocked, on hold, or behind-and-not-moving. The board's
  // pace cannot finish these, so they cap an otherwise green verdict down to warn.
  const stuck = scope.reduce((n, c) => (cardStuckState(c, boardIssues, now, cal) ? n + 1 : n), 0);
  const rag: Rag = stuck > 0 && fc.rag === 'good' ? 'warn' : fc.rag;

  return { prob: fc.targetProb, rag, remaining: fc.remaining, stuck, target };
}
